import { formatLocalDateTime, formatLocalDate, localDateInputValue } from '../utils/localDateTime';
import { confirmApp } from '../common/appDialog';
import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    collection,
    getDocs,
    onSnapshot,
} from 'firebase/firestore';
import type { UserProfile, Organization, HelpdeskTicket } from '@types';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';
import { adminRoleLabel, canAccessPlatformManagement, canManageMsaDocuments } from '../common/adminAccess';
import PlatformOrganizationManagement from './PlatformOrganizationManagement';
import PlatformAdminManagement from './PlatformAdminManagement';
import PlatformAdminProfile from './PlatformAdminProfile';
import { openMfaRecovery } from '../common/mfa';
import { mfaStyles } from './mfaStyles';
import { managementButtonStyles as buttonStyles } from '../common/managementButtonStyles';
import PlatformProductManagement from './PlatformProductManagement';
import PlatformUserEditorModal, { type PlatformUserUpdate } from './PlatformUserEditorModal';
import { USER_ROLES, getUserRoleLabel } from '../utils/userDisplayLabels';
import ManagementChatNotifications from './support/ManagementChatNotifications';
import GlobalChatsPanel from './support/GlobalChatsPanel';
import CommunicationTabs, { type CommunicationTab } from './support/CommunicationTabs';
import FeedbackPanel from './support/FeedbackPanel';
import SupportQueuePanel from './support/SupportQueuePanel';
import PlatformMsaManagement from './PlatformMsaManagement';
import PlatformModels from './PlatformModels';
import ModelRetirementNotifications from './ModelRetirementNotifications';
import PlatformServiceRequestsPanel from './PlatformServiceRequestsPanel';
import HelpLibrary from './HelpLibrary';

interface NDAApplication {
    id: string;
    fullName: string;
    email: string;
    testerRole: 'internal' | 'external-contractor' | 'external-tester';
    testingScope: string;
    agreementVersion: string;
    agreedAt: string;
    sessionId: string;
    status: 'pending' | 'approved' | 'denied';
    approvedAt?: string;
    deniedAt?: string;
    recordedAt?: string;
}

interface SuperAdminDashboardProps {
    currentUser: UserProfile;
    onLogout: () => void;
}

type AdminTab = 'orgs' | 'users' | 'admins' | 'serviceRequests' | 'support' | 'chats' | 'feedback' | 'beta' | 'products' | 'msa' | 'models';

const ROLE_LABELS: Record<string, string> = {
    'internal': 'Internal Tester',
    'external-contractor': 'Contractor',
    'external-tester': 'External Beta',
};

// ─── Icon helpers ──────────────────────────────────────────────────────────────
const Icon = {
    org: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    users: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    ticket: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>,
    products: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12V8H4v4m16 0v8H4v-8m16 0H4m4-4V4h8v4" /></svg>,
    trash: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    shield: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    download: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
    mail: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    ban: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
    check: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    nda: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
};

// ─── Status badges ─────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const colors: Record<string, string> = {
        active: 'bg-emerald-100 text-emerald-700',
        pending: 'bg-amber-100 text-amber-700',
        suspended: 'bg-red-100 text-red-700',
        open: 'bg-red-100 text-red-700',
        'in-progress': 'bg-amber-100 text-amber-700',
        resolved: 'bg-emerald-100 text-emerald-700',
        closed: 'bg-slate-100 text-slate-500',
        owner: 'bg-violet-100 text-violet-700',
        admin: 'bg-blue-100 text-blue-700',
        employee: 'bg-slate-100 text-slate-600',
        service_requester: 'bg-indigo-100 text-indigo-700',
        service_requestor: 'bg-indigo-100 text-indigo-700',
        trainer: 'bg-teal-100 text-teal-700',
        learner: 'bg-sky-100 text-sky-700',
        individual: 'bg-slate-100 text-slate-600',
        business: 'bg-blue-100 text-blue-700',
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide ${colors[status] ?? 'bg-slate-100 text-slate-500'}`}>
            {getUserRoleLabel(status)}
        </span>
    );
};

// ─── CSV Export helper ─────────────────────────────────────────────────────────
const exportUsersCSV = (users: UserProfile[]) => {
    const headers = ['Display Name', 'Email', 'Role', 'Status', 'Organization ID', 'Last Login'];
    const rows = users.map(u => [
        u.displayName || '',
        u.email || '',
        u.role || '',
        u.status || '',
        u.organizationId || '',
        u.lastLogin ? formatLocalDateTime(u.lastLogin) : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inteloptics-users-${localDateInputValue()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
};

// ─── Main Component ────────────────────────────────────────────────────────────
const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ currentUser, onLogout }) => {
    const [activeTab, setActiveTab] = useState<AdminTab>('orgs');
    const [organizationToOpen, setOrganizationToOpen] = useState<string | null>(null);
    const [administratorToEdit, setAdministratorToEdit] = useState<string | null>(null);
    const [orgs, setOrgs] = useState<(Organization & { id: string })[]>([]);
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [tickets, setTickets] = useState<HelpdeskTicket[]>([]);
    const [ndaApps, setNdaApps] = useState<NDAApplication[]>([]);
    const [ndaFilter, setNdaFilter] = useState<'all' | 'pending' | 'approved' | 'denied'>('pending');
    const [ndaActioning, setNdaActioning] = useState<string | null>(null);
    const [ndaLoadError, setNdaLoadError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [showProfile, setShowProfile] = useState(false);
    const [adminProfile, setAdminProfile] = useState(currentUser);
    const [userToEdit, setUserToEdit] = useState<UserProfile | null>(null);
    const [userEditBusy, setUserEditBusy] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    // ── Toast helper ──
    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    // ── Load all data on tab change ──
    useEffect(() => {
        if (!currentUser.adminRole) return;

        const load = async () => {
            setIsLoading(true);
            try {
                if (activeTab === 'orgs') {
                    setIsLoading(false);
                } else if (activeTab === 'users') {
                    const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
                    const response = await callable({ action: 'listDirectory', data: {} });
                    const directory = response.data as { users: UserProfile[]; organizations: (Organization & { id: string })[] };
                    setUsers(directory.users);
                    setOrgs(directory.organizations);
                } else if (activeTab === 'admins') {
                    setIsLoading(false);
                } else if (activeTab === 'beta') {
                    setNdaLoadError(null);
                    try {
                        const snap = await getDocs(collection(db, 'tester_nda_agreements'));
                        const apps = snap.docs
                            .map(d => ({ id: d.id, ...d.data() } as NDAApplication))
                            .sort((a, b) => (b.agreedAt ?? '').localeCompare(a.agreedAt ?? ''));
                        setNdaApps(apps);
                    } catch (fnErr: any) {
                        const msg = fnErr?.message || 'Unknown error';
                        setNdaLoadError(`Could not load applications: ${msg}`);
                        console.error('[Beta tab] Firestore read error:', fnErr);
                    }
                }
            } catch (e) {
                console.error('[SuperAdmin] Load error', e);
                showToast('Failed to load data.', 'error');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [activeTab, currentUser.adminRole]);

    if (!currentUser.adminRole) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-500 mb-4">{Icon.shield}</div>
                <h2 className="text-2xl font-bold text-slate-800">Access Denied</h2>
                <p className="text-slate-500 mt-2">This portal is restricted to platform administrators.</p>
                <button onClick={onLogout} className="mt-6 text-blue-600 hover:underline text-sm">Log Out</button>
            </div>
        );
    }

    // ── User actions ──
    const handleUserStatus = async (email: string, newStatus: 'active' | 'suspended') => {
        const label = newStatus === 'suspended' ? 'suspend' : 'reactivate';
        if (!await confirmApp(`Are you sure you want to ${label} ${email}?`)) return;
        try {
            const user = users.find(candidate => candidate.email === email);
            if (!user?.organizationId)
                throw new Error('Organization user not found.');
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            await callable({ action: 'updateUser', data: { organizationId: user.organizationId, email, displayName: user.displayName, role: user.role, status: newStatus } });
            setUsers(prev => prev.map(u => u.email === email ? { ...u, status: newStatus } as UserProfile : u));
            showToast(`User ${label}d successfully.`);
        } catch (e) {
            showToast(`Failed to ${label} user.`, 'error');
        }
    };

    const handleUserRole = async (email: string, newRole: UserProfile['role']) => {
        if (email === currentUser.email) { showToast("You can't change your own role.", 'error'); return; }
        try {
            const user = users.find(candidate => candidate.email === email);
            if (!user?.organizationId)
                throw new Error('Organization user not found.');
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            await callable({ action: 'updateUser', data: { organizationId: user.organizationId, email, displayName: user.displayName, role: newRole, status: user.status } });
            setUsers(prev => prev.map(u => u.email === email ? { ...u, role: newRole } : u));
            showToast(`Role updated to ${newRole}.`);
        } catch (e) {
            showToast('Failed to update role.', 'error');
        }
    };

    const handleDeleteUser = async (user: UserProfile) => {
        if (!user.organizationId || !await confirmApp(`Delete ${user.displayName || user.email}? Their access will be disabled.`))
            return;
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            await callable({ action: 'deleteUser', data: { organizationId: user.organizationId, email: user.email } });
            setUsers(previous => previous.filter(candidate => candidate.email !== user.email));
            showToast('User deleted successfully.');
        } catch (error: any) {
            showToast(error?.message || 'Failed to delete user.', 'error');
        }
    };

    const handleEditUser = async (update: PlatformUserUpdate) => {
        if (!userToEdit?.organizationId)
            return;
        setUserEditBusy(true);
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            const action = update.adminRole ? 'promoteUserAdmin' : 'updateUser';
            await callable({
                action,
                data: {
                    ...update,
                    email: userToEdit.email,
                    organizationId: userToEdit.organizationId,
                },
            });
            setUsers(previous => previous.map(user => user.email === userToEdit.email ? {
                ...user,
                displayName: update.displayName,
                role: update.role,
                status: update.status,
                adminRole: update.adminRole || undefined,
                adminRoles: update.adminRole ? update.adminRoles : undefined,
                regions: update.adminRole && update.adminRoles.includes('Regional Consultant') ? update.regions : [],
            } : user));
            setUserToEdit(null);
            showToast(update.adminRole ? 'User promoted successfully.' : 'User updated successfully.');
        } catch (error: any) {
            showToast(error?.message || 'Failed to update user.', 'error');
        } finally {
            setUserEditBusy(false);
        }
    };

    // ── NDA Approval action ──
    const handleNDAAction = async (docId: string, action: 'approve' | 'deny') => {
        const app = ndaApps.find(a => a.id === docId);
        if (!app) return;
        const label = action === 'approve' ? 'approve' : 'deny';
        if (!await confirmApp(`${action === 'approve' ? 'Approve' : 'Deny'} access for ${app.fullName} (${app.email})?`)) return;
        setNdaActioning(docId);
        try {
            const fns = getFunctions(undefined, 'us-central1');
            const adminApprove = httpsCallable(fns, 'adminNDAApproval');
            const result: any = await adminApprove({ action, docId });
            if (result.data?.success) {
                setNdaApps(prev => prev.map(a => a.id === docId
                    ? { ...a, status: action === 'approve' ? 'approved' : 'denied' }
                    : a
                ));
                showToast(`${app.fullName} ${label}d. Access email sent.`);
            } else {
                showToast(result.data?.message || `Could not ${label}.`, 'error');
            }
        } catch (e: any) {
            showToast(e?.message || `Failed to ${label}.`, 'error');
        } finally {
            setNdaActioning(null);
        }
    };

    const tabs: { key: AdminTab; label: string; icon: React.ReactNode; badge?: number }[] = [
        { key: 'orgs', label: 'Organizations', icon: Icon.org },
        { key: 'users', label: 'Users', icon: Icon.users },
        { key: 'serviceRequests', label: 'Service Requests', icon: Icon.ticket },
        { key: 'support', label: 'Communication', icon: Icon.mail, badge: tickets.filter(t => t.status === 'open').length },
        ...(canAccessPlatformManagement(currentUser.adminRole) ? [
            { key: 'products' as AdminTab, label: 'Platform Management', icon: Icon.products },
        ] : []),
    ];

    const primaryTabActive = (key: AdminTab) => {
        if (key === 'support')
            return ['support', 'chats', 'feedback'].includes(activeTab);
        if (key === 'users')
            return activeTab === 'users' || activeTab === 'admins';
        if (key === 'products')
            return activeTab === 'products' || activeTab === 'msa' || activeTab === 'models';
        return activeTab === key;
    };

    const filteredUsers = users.filter(u =>
        !search || u.email?.toLowerCase().includes(search.toLowerCase()) || u.displayName?.toLowerCase().includes(search.toLowerCase())
    ).sort((left, right) => (left.displayName || left.email).localeCompare(right.displayName || right.email, undefined, { sensitivity: 'base' }));
    const organizationName = (organizationId?: string) => {
        const organization = orgs.find(candidate => candidate.id === organizationId);
        return organization ? getOrganizationDisplayName(organization) : organizationId || '—';
    };
    const openOrganization = (organizationId?: string) => {
        if (!organizationId)
            return;
        setOrganizationToOpen(organizationId);
        setActiveTab('orgs');
    };

    return (
        <div className="animate-fade-in max-w-7xl mx-auto py-8 px-4 relative">
            <ManagementChatNotifications currentUser={currentUser} />
            {currentUser.adminRole === 'Super' && <ModelRetirementNotifications onOpen={() => setActiveTab('models')} />}

            {/* ── Toast ── */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-semibold flex items-center gap-2 transition-all ${
                    toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
                }`}>
                    {toast.type === 'success' ? Icon.check : Icon.ban}
                    {toast.msg}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 border-b border-slate-200 pb-5">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center text-white shadow-md">
                        {Icon.shield}
                    </div>
                    <div>
                        <h1 className="text-2xl font-extrabold text-slate-900">Management Portal</h1>
                        <p className="text-xs text-slate-400 font-mono">Platform operations &amp; monitoring</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => setShowHelp(true)} className="rounded-lg px-3 py-1.5 text-xs font-bold text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800">
                        Help &amp; Guidance
                    </button>
                    <button onClick={() => setShowProfile(true)} className="rounded-lg px-3 py-1.5 text-xs font-bold text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800">
                        Manage Profile
                    </button>
                    <span className="text-[10px] font-bold bg-violet-100 text-violet-700 px-3 py-1 rounded-full uppercase tracking-widest border border-violet-200">
                        {adminRoleLabel(currentUser.adminRole)}
                    </span>
                    <button
                        onClick={onLogout}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    >
                        Log Out
                    </button>
                </div>
            </div>

            {showProfile && (
                <PlatformAdminProfile
                    currentUser={adminProfile}
                    onClose={() => setShowProfile(false)}
                    onSaved={profile => {
                        setAdminProfile(previous => ({ ...previous, ...profile }));
                        showToast('Profile updated.');
                    }}
                />
            )}

            {showHelp && <HelpLibrary onClose={() => setShowHelp(false)} fullWorkspaceAccess userRole={currentUser.role} />}

            {/* Tabs */}
            <div className="flex flex-wrap items-center gap-1 bg-slate-100 p-1.5 rounded-2xl mb-8 w-fit shadow-inner">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all relative ${
                            primaryTabActive(tab.key)
                                ? 'bg-white text-blue-600 shadow-sm ring-1 ring-slate-200'
                                : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                        }`}
                    >
                        {tab.icon}
                        {tab.label}
                        {tab.badge !== undefined && tab.badge > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow-sm ring-2 ring-white">
                                {tab.badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {['support', 'chats', 'feedback'].includes(activeTab) && <div className="mb-6"><CommunicationTabs activeTab={activeTab as CommunicationTab} onChange={setActiveTab} /></div>}
            <div hidden={activeTab !== 'support'}><SupportQueuePanel currentUser={currentUser} enabled={activeTab === 'support'} onTicketsChanged={setTickets} /></div>

            {['users', 'admins'].includes(activeTab) && ['Super', 'Global'].includes(String(currentUser.adminRole)) && (
                <div className="mb-6 flex w-fit items-center gap-1 rounded-xl bg-slate-100 p-1">
                    <button onClick={() => setActiveTab('users')} className={`rounded-lg px-5 py-2 text-sm font-bold ${activeTab === 'users' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>All Users</button>
                    <button onClick={() => setActiveTab('admins')} className={`rounded-lg px-5 py-2 text-sm font-bold ${activeTab === 'admins' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>Admins</button>
                </div>
            )}

            {['products', 'msa', 'models'].includes(activeTab) && canAccessPlatformManagement(currentUser.adminRole) && (
                <div className="mb-6 flex w-fit items-center gap-1 rounded-xl bg-slate-100 p-1">
                    <button onClick={() => setActiveTab('products')} className={`rounded-lg px-5 py-2 text-sm font-bold ${activeTab === 'products' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>Products</button>
                    <button onClick={() => setActiveTab('msa')} className={`rounded-lg px-5 py-2 text-sm font-bold ${activeTab === 'msa' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>MSA</button>
                    {currentUser.adminRole === 'Super' && <button onClick={() => setActiveTab('models')} className={`rounded-lg px-5 py-2 text-sm font-bold ${activeTab === 'models' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>Models</button>}
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-24 text-slate-400">
                    <svg className="animate-spin w-8 h-8 mr-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                    Loading platform data…
                </div>
            ) : (
                <>
                    {/* ── ORGANIZATIONS TAB ── */}
                    {activeTab === 'orgs' && <PlatformOrganizationManagement currentUser={adminProfile} openOrganizationId={organizationToOpen} onOpenOrganizationHandled={() => setOrganizationToOpen(null)} />}

                    {activeTab === 'admins' && <PlatformAdminManagement currentUser={adminProfile} editEmail={administratorToEdit} onEditHandled={() => setAdministratorToEdit(null)} />}

                    {activeTab === 'products' && canAccessPlatformManagement(currentUser.adminRole) && <PlatformProductManagement />}
                    {activeTab === 'msa' && canManageMsaDocuments(currentUser.adminRole) && <PlatformMsaManagement />}
                    {activeTab === 'models' && currentUser.adminRole === 'Super' && <PlatformModels />}

                    {activeTab === 'serviceRequests' && <PlatformServiceRequestsPanel currentUser={currentUser} />}

                    {/* ── USERS TAB ── */}
                    {activeTab === 'users' && (
                        <div className="space-y-4 animate-fade-in">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="text"
                                        placeholder="Search by name or email…"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="border border-slate-300 rounded-lg px-4 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                    />
                                    <p className="text-sm text-slate-400">{filteredUsers.length} of {users.length} users</p>
                                </div>
                                <button
                                    onClick={() => exportUsersCSV(filteredUsers)}
                                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm ${buttonStyles.primary}`}
                                >
                                    {Icon.download}
                                    Export CSV
                                </button>
                            </div>
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200">
                                        <tr>
                                            <th className="px-5 py-3">Name / Email</th>
                                            <th className="px-5 py-3">Organization</th>
                                            <th className="px-5 py-3">Role</th>
                                            <th className="px-5 py-3">Status</th>
                                            <th className="px-5 py-3">Last Login</th>
                                            <th className="px-5 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {filteredUsers.map((u, i) => {
                                            const isSelf = u.email === currentUser.email;
                                            const isSuper = u.adminRole === 'Super';
                                            const isSuspended = (u.status as string) === 'suspended';
                                            return (
                                                <tr key={i} className={`hover:bg-slate-50 transition-colors ${isSuspended ? 'opacity-60' : ''}`}>
                                                    <td className="px-5 py-3">
                                                        <p className="font-semibold text-slate-800">{u.displayName || '—'}</p>
                                                        <p className="text-xs text-slate-400 font-mono">{u.email}</p>
                                                    </td>
                                                    <td className="px-5 py-3 max-w-[180px] truncate" title={organizationName(u.organizationId)}>
                                                        <button onClick={() => openOrganization(u.organizationId)} disabled={!u.organizationId} className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline disabled:text-slate-400 disabled:no-underline">{organizationName(u.organizationId)}</button>
                                                    </td>
                                                    <td className="px-5 py-3">
                                                        {isSelf || isSuper || u.role === 'owner' ? (
                                                            <StatusBadge status={u.role || 'owner'} />
                                                        ) : (
                                                            <select
                                                                value={u.role}
                                                                onChange={e => handleUserRole(u.email, e.target.value as UserProfile['role'])}
                                                                className="text-xs font-semibold rounded-lg p-1.5 border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                                            >
                                                                {USER_ROLES.filter(role => role !== 'owner').map(role => <option key={role} value={role}>{getUserRoleLabel(role)}</option>)}
                                                            </select>
                                                        )}
                                                    </td>
                                                    <td className="px-5 py-3"><StatusBadge status={u.status || 'pending'} /></td>
                                                    <td className="px-5 py-3 text-xs text-slate-400">
                                                        {u.lastLogin ? formatLocalDateTime(u.lastLogin) : '—'}
                                                    </td>
                                                    <td className="px-5 py-3">
                                                        <div className="flex items-center justify-end gap-2">
                                                            {currentUser.adminRole === 'Super' && !isSelf && !u.isDeleted && !['deleted', 'suspended'].includes(u.status) && <button onClick={() => openMfaRecovery(u.email)} className={mfaStyles.recoveryAction}>Reset MFA</button>}
                                                            {!isSelf && !isSuper && <button
                                                                onClick={() => {
                                                                    if (u.adminRole) {
                                                                        setAdministratorToEdit(u.email);
                                                                        setActiveTab('admins');
                                                                    } else {
                                                                        setUserToEdit(u);
                                                                    }
                                                                }}
                                                                className="text-xs font-bold text-blue-600 hover:text-blue-800"
                                                            >
                                                                Edit
                                                            </button>}
                                                            {/* Email */}
                                                            <a
                                                                href={`mailto:${u.email}`}
                                                                className="text-slate-400 hover:text-blue-600 transition-colors"
                                                                title={`Email ${u.email}`}
                                                            >
                                                                {Icon.mail}
                                                            </a>
                                                            {/* Suspend / Reactivate */}
                                                            {!isSelf && !u.adminRole && (
                                                                <button
                                                                    onClick={() => handleUserStatus(u.email, isSuspended ? 'active' : 'suspended')}
                                                                    className={`transition-colors ${isSuspended ? 'text-emerald-500 hover:text-emerald-700' : 'text-slate-400 hover:text-red-600'}`}
                                                                    title={isSuspended ? 'Reactivate user' : 'Suspend user'}
                                                                >
                                                                    {isSuspended ? Icon.check : Icon.ban}
                                                                </button>
                                                            )}
                                                            {!isSelf && !u.adminRole && u.role !== 'owner' && (
                                                                <button
                                                                    onClick={() => handleDeleteUser(u)}
                                                                    className="text-slate-400 transition-colors hover:text-red-700"
                                                                    title="Delete user"
                                                                >
                                                                    {Icon.trash}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {filteredUsers.length === 0 && (
                                    <p className="p-8 text-center text-slate-400 italic">No users found.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'chats' && <GlobalChatsPanel currentUser={currentUser} />}
                    {activeTab === 'feedback' && <FeedbackPanel />}

                    {/* ── BETA TESTER TAB — developer account only ── */}
                    {activeTab === 'beta' && (currentUser.adminRole === 'Super' || currentUser.adminRole === 'Global') && (
                        <div className="animate-fade-in space-y-5">
                            {/* Header row */}
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <h2 className="text-xl font-bold text-slate-800">Beta Tester NDA Applications</h2>
                                    <p className="text-sm text-slate-500 mt-0.5">
                                        Review and action NDA submissions from prospective beta testers.
                                        Approved testers receive a magic-link access email automatically.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    {(['all', 'pending', 'approved', 'denied'] as const).map(f => (
                                        <button
                                            key={f}
                                            onClick={() => setNdaFilter(f)}
                                            className={`px-3 py-1.5 rounded-lg font-semibold capitalize transition-colors ${ndaFilter === f
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                        >
                                            {f}
                                            {f !== 'all' && (
                                                <span className="ml-1.5 text-[10px] font-bold bg-white/30 px-1 rounded">
                                                    {ndaApps.filter(a => a.status === f).length}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Stat strip */}
                            <div className="grid grid-cols-3 gap-4">
                                {[
                                    { label: 'Pending', count: ndaApps.filter(a => a.status === 'pending').length, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
                                    { label: 'Approved', count: ndaApps.filter(a => a.status === 'approved').length, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
                                    { label: 'Denied', count: ndaApps.filter(a => a.status === 'denied').length, color: 'text-red-500', bg: 'bg-red-50 border-red-200' },
                                ].map(s => (
                                    <div key={s.label} className={`rounded-xl border p-4 ${s.bg}`}>
                                        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{s.label}</p>
                                        <p className={`text-3xl font-extrabold mt-1 ${s.color}`}>{s.count}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Load error */}
                            {ndaLoadError && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium flex items-center gap-3">
                                    <span className="text-red-500 text-xl">⚠️</span>
                                    <div>
                                        <p className="font-bold">Failed to load applications</p>
                                        <p className="text-xs mt-0.5 font-mono">{ndaLoadError}</p>
                                    </div>
                                    <button
                                        onClick={() => { setActiveTab('orgs'); setTimeout(() => setActiveTab('beta'), 50); }}
                                        className="ml-auto text-xs font-bold px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                    >Retry</button>
                                </div>
                            )}

                            {/* Application cards */}
                            {(ndaFilter === 'all' ? ndaApps : ndaApps.filter(a => a.status === ndaFilter)).length === 0 ? (
                                <div className="text-center py-16 text-slate-400">
                                    <div className="text-4xl mb-3">📋</div>
                                    <p className="font-semibold">No {ndaFilter === 'all' ? '' : ndaFilter} applications</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {(ndaFilter === 'all' ? ndaApps : ndaApps.filter(a => a.status === ndaFilter)).map(app => (
                                        <div key={app.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                                            app.status === 'pending' ? 'border-amber-200' :
                                            app.status === 'approved' ? 'border-emerald-200' : 'border-red-200'
                                        }`}>
                                            <div className="px-6 py-4 flex flex-wrap items-start gap-4">
                                                {/* Status dot + Name */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                                            app.status === 'pending' ? 'bg-amber-400' :
                                                            app.status === 'approved' ? 'bg-emerald-500' : 'bg-red-400'
                                                        }`} />
                                                        <span className={`text-[10px] font-black uppercase tracking-widest ${
                                                            app.status === 'pending' ? 'text-amber-600' :
                                                            app.status === 'approved' ? 'text-emerald-600' : 'text-red-500'
                                                        }`}>{app.status}</span>
                                                        <span className="text-[10px] text-slate-400 font-mono">#{app.id.slice(-8)}</span>
                                                    </div>
                                                    <p className="text-base font-bold text-slate-800">{app.fullName}</p>
                                                    <a href={`mailto:${app.email}`} className="text-sm text-blue-600 hover:underline font-medium">{app.email}</a>
                                                </div>

                                                {/* Meta */}
                                                <div className="text-right text-xs text-slate-500 space-y-0.5 flex-shrink-0">
                                                    <p><span className="font-semibold text-slate-700">Role:</span> {ROLE_LABELS[app.testerRole] || app.testerRole}</p>
                                                    <p><span className="font-semibold text-slate-700">Scope:</span> {app.testingScope}</p>
                                                    <p><span className="font-semibold text-slate-700">NDA:</span> {app.agreementVersion}</p>
                                                    <p className="text-slate-400">{app.agreedAt ? formatLocalDateTime(app.agreedAt) : '—'}</p>
                                                </div>

                                                {/* Action buttons — only for pending */}
                                                {app.status === 'pending' && (
                                                    <div className="flex gap-2 flex-shrink-0 items-center">
                                                        <button
                                                            onClick={() => handleNDAAction(app.id, 'approve')}
                                                            disabled={ndaActioning === app.id}
                                                            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-bold rounded-lg transition-colors"
                                                        >
                                                            {Icon.check}
                                                            {ndaActioning === app.id ? 'Processing…' : 'Approve'}
                                                        </button>
                                                        <button
                                                            onClick={() => handleNDAAction(app.id, 'deny')}
                                                            disabled={ndaActioning === app.id}
                                                            className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-slate-300 text-white text-xs font-bold rounded-lg transition-colors"
                                                        >
                                                            {Icon.ban}
                                                            Deny
                                                        </button>
                                                    </div>
                                                )}
                                                {app.status === 'approved' && (
                                                    <span className="text-[11px] text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg flex-shrink-0">
                                                        ✅ Access granted · {app.approvedAt ? formatLocalDate(app.approvedAt) : ''}
                                                    </span>
                                                )}
                                                {app.status === 'denied' && (
                                                    <span className="text-[11px] text-red-600 font-bold bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg flex-shrink-0">
                                                        ❌ Denied · {app.deniedAt ? formatLocalDate(app.deniedAt) : ''}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Session ID footer */}
                                            <div className="px-6 py-2 bg-slate-50 border-t border-slate-100">
                                                <p className="text-[10px] text-slate-400 font-mono">Session: {app.sessionId || '—'}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </>
            )}
            {userToEdit && <PlatformUserEditorModal user={userToEdit} busy={userEditBusy} canPromote={currentUser.adminRole === 'Super'} onClose={() => setUserToEdit(null)} onSave={handleEditUser} />}
        </div>
    );
};

export default SuperAdminDashboard;
